import type {
  Device,
  Bundle as FhirBundle,
  BundleEntry as FhirBundleEntry,
  FhirResource,
  Organization,
  Patient,
  Practitioner,
  PractitionerRole,
  RelatedPerson,
  Resource,
} from 'fhir/r4';

import type { AnyPerson } from '../mapping';

export type ResourceType<T extends FhirResource = FhirResource> =
  T['resourceType'];

/**
 * The entry in a bundle for a collection FHIR `GET` operation using the `BaseAPI`
 */
export interface BundleEntry<T extends Resource>
  extends Omit<FhirBundleEntry, 'resource'> {
  resource: T;
}

/**
 * The bundle returned for a collection FHIR `GET` operation using the `BaseAPI`
 */
export interface Bundle<T extends Resource> extends Omit<FhirBundle, 'entry'> {
  entry?: Array<BundleEntry<T>>;
}

/**
 * The bundle returned for a collection FHIR `POST` operation using a mixture of different
 * resources.
 *
 * @example
 * const createPatientBundle: MixedBundle<Patient | Device> = {
 *   // ... other props
 *   entry: [ patientEntry, deviceEntry1, deviceEntry2 ]
 * }
 */
export interface MixedBundle<TMixedBundleOptions extends FhirResource>
  extends Omit<FhirBundle, 'entry'> {
  entry: Array<BundleEntry<TMixedBundleOptions>>;
}

export type WithId<TResource extends FhirResource> = TResource & {
  id: string;
};

export type Editor = WithId<
  | Practitioner
  | PractitionerRole
  | Organization
  | Device
  | Patient
  | RelatedPerson
>;

export type EditorResourceType = Editor['resourceType'];

export type CommonKeys<T, R = {}> = R extends T
  ? keyof T & CommonKeys<Exclude<T, R>>
  : keyof T;
export type Intersection<T> = Pick<T, CommonKeys<T>>;

export type AnyPersonCommon<
  TResource extends AnyPerson['resourceType'] = AnyPerson['resourceType'],
> = Intersection<AnyPerson> & {
  resourceType: TResource;
};
